import { useCallback } from 'react';
import {
  useProjectStore,
  type TrackingConfig as StoreTrackingConfig,
} from '../store/projectStore';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';
import { completeManualSessionIfNeeded } from '../utils/manualSession';
import { syncTorchTrackingBrowsersFromElectron } from '../utils/registerTorchTrackingBrowser';

export interface TrackingConfig {
  recordNetworkTraffic: boolean;
  recordPageScreenshots: boolean;
  credentialId?: string;
  responseMimeTypes?: string[];
  skipNoiseUrlExtensions?: boolean;
}

export function useTracking() {
  const { isTrackingEnabled, selectedTarget, enableTracking, disableTracking } =
    useProjectStore();

  const startTracking = useCallback(
    async (config: TrackingConfig) => {
      if (!selectedTarget) {
        console.error('No target selected for tracking');

        return { success: false, error: 'No target selected' };
      }

      if (typeof window === 'undefined' || !window.electronAPI) {
        return { success: false, error: 'Electron API not available' };
      }

      let crawlerSessionId: string | undefined;

      try {
        const authHeaders = await getAuthHeaders();
        const { body: manualSessionBody } =
          await applicationsClient.getOrCreateManualCrawlerSession({
            params: { id: selectedTarget.id },
            body: { credentialId: config.credentialId ?? null },
            overrideClientOptions: {
              baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
            },
            extraHeaders: { ...authHeaders },
          });

        crawlerSessionId = (
          manualSessionBody as { crawlerSessionId?: string } | undefined
        )?.crawlerSessionId;

        if (typeof crawlerSessionId !== 'string' || !crawlerSessionId.trim()) {
          return {
            success: false,
            error:
              'Failed to get or create manual crawler session; cannot start tracking without a valid session',
          };
        }

        const result = await window.electronAPI.tracking.start({
          applicationId: selectedTarget.id,
          companyId: selectedTarget.tenantId || '',
          credentialId: config.credentialId,
          crawlerSessionId,
          trackingConfig: {
            recordNetworkTraffic: config.recordNetworkTraffic,
            recordPageScreenshots: config.recordPageScreenshots,
            responseMimeTypes: config.responseMimeTypes,
            skipNoiseUrlExtensions: config.skipNoiseUrlExtensions,
          },
          applicationUrl: selectedTarget.url,
          applicationDomains: selectedTarget.domains || [],
        });

        if (result.success) {
          const storeConfig: StoreTrackingConfig = {
            recordNetworkTraffic: config.recordNetworkTraffic,
            recordPageScreenshots: config.recordPageScreenshots,
            responseMimeTypes: config.responseMimeTypes,
            skipNoiseUrlExtensions: config.skipNoiseUrlExtensions,
          };

          enableTracking(
            storeConfig,
            config.credentialId,
            crawlerSessionId,
            selectedTarget.id,
          );
          await syncTorchTrackingBrowsersFromElectron({
            trackingTargetId: selectedTarget.id,
          });

          return result;
        }

        // Local start failed; complete the backend session so it does not stay RUNNING
        await completeManualSessionIfNeeded(
          selectedTarget.id,
          crawlerSessionId,
        );

        return result;
      } catch (error) {
        console.error('Error starting tracking:', error);
        // Session was created but local start failed; complete it to avoid orphan RUNNING session
        if (crawlerSessionId && selectedTarget?.id) {
          await completeManualSessionIfNeeded(
            selectedTarget.id,
            crawlerSessionId,
          );
        }

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [selectedTarget, enableTracking],
  );

  const stopTracking = useCallback(async () => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      return { success: false, error: 'Electron API not available' };
    }

    const {
      trackingTargetId: applicationId,
      trackingCrawlerSessionId: crawlerSessionId,
    } = useProjectStore.getState();

    try {
      // Stop local tracking first; only then complete the backend session.
      const result = await window.electronAPI.tracking.stop();

      if (!result.success) {
        return result;
      }

      // Local stop succeeded — always complete backend session and disable tracking
      await completeManualSessionIfNeeded(applicationId, crawlerSessionId);
      disableTracking();

      return result;
    } catch (error) {
      console.error('Error stopping tracking:', error);
      // Even on failure, attempt to clean up backend session to avoid orphaned RUNNING sessions
      await completeManualSessionIfNeeded(applicationId, crawlerSessionId);
      disableTracking();

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [disableTracking]);

  return {
    isTrackingEnabled,
    startTracking,
    stopTracking,
  };
}
